import {COUNTRY_CODES} from 'common/constants/countries'
import {getSchoolSystemsDetails} from 'setup/redux/Actions/CsvImport/csvImport'
import {getTimetablePrefill} from 'setup/redux/Actions/Onboarding/getTimetablePrefill'

import {
  StoredLevelDetail,
  StoredSchoolSystemDetails,
  normalizeSchoolSystemDetailsResponse,
  readStoredSchoolSystemDetails,
  writeStoredSchoolSystemDetails,
} from './schoolSystemDetailsCache'

/**
 * Curriculum prefill (GET timetable/curricula + GET timetable/prefill) — the wizard-side half of
 * docs/curriculum-prefill-frontend-integration.md.
 *
 * `timetable/curricula` feeds the Setup Basics curriculum picker (country-scoped, national boards
 * first). `timetable/prefill` returns, per level, the grades, school timings, lecture length,
 * breaks and subjects with a weekly frequency, plus the working week and academic session. This
 * module normalises those payloads, caches them per curriculum (localStorage, like the legacy
 * school-system details) and projects them onto the shapes the wizard already consumes:
 *
 *  - `StoredSchoolSystemDetails` (levels → classes → subjects) for the Academic Structure and
 *    Assign Subjects steps, so those screens keep working unchanged;
 *  - Institution Setup defaults (session, timings, breaks) for the Step 2 review page;
 *  - a per-level subject → frequency map for Lesson Planning.
 *
 * Curricula picked from the old `csv-import/school-systems` list (or typed under "Other") carry
 * no code; for those `loadCurriculumDefaults` still falls back to the legacy details endpoint,
 * so a tenant that resumes with an older selection loses nothing.
 */

export const ONBOARDING_CURRICULUM_PREFILL_KEY = 'onboarding_curriculum_prefill'

export type CurriculumScope = 'national' | 'international'

/** One row of `timetable/curricula`. `name` is what the wizard stores in `manualData.curriculums`. */
export type CurriculumOption = {
  code: string
  name: string
  scope: CurriculumScope
  awardingBody: string | null
  /** Alpha-2 of the country a national board belongs to; null for international curricula. */
  country: string | null
  /** Short code ("FBISE"), sent as `curriculum_short_code` on levels; null when not sent. */
  shortCode?: string | null
}

export type CurriculaCatalog = {
  country: {code: string; name: string} | null
  /** The first `nationalCount` entries of `curricula` are the local boards, in "most likely first" order. */
  nationalCount: number
  curricula: CurriculumOption[]
  /**
   * Curricula most commonly taught in the country, sorted by `rank` (`common_curricula`).
   * Every entry is also in `curricula` (same code and name), so picking one behaves exactly
   * like picking it from the full list. Empty when the API does not send the field.
   */
  commonCurricula: CurriculumOption[]
  /** `curated` (researched for this country) or `default` (own boards + Cambridge + IB); null when absent. */
  commonCurriculaSource: 'curated' | 'default' | null
}

/** Provenance flags from the prefill's `sources` block (structure/timings/session/subjects/…). */
export type PrefillSources = Record<string, string>

/** One default-selected subject of a level (`levels[].subjects`). */
export type PrefillSubject = {
  name: string
  /** Already in the backend's `"5/week"` form. */
  frequency: string
  periodsPerWeek: number | null
  /** `default_selected`; every entry of `subjects` is pre-selected, so true when sent. */
  defaultSelected?: boolean
  /** Why it is pre-selected: 'official' | 'local' | 'assumption'. */
  basis?: string | null
  /** `frequency_source`: where the weekly frequency comes from. */
  frequencySource?: string | null
}

/** One subject a level offers, selected or not (`levels[].available_subjects`). */
export type PrefillAvailableSubject = {
  name: string
  frequency: string
  periodsPerWeek: number | null
  /** Programme variants the subject belongs to (e.g. "Pre-Medical"); [] when not sent. */
  groups: string[]
  /** Grades that offer the subject; [] = every grade of the level. */
  grades: string[]
  /** Grades where it is pre-selected; [] when not sent. */
  defaultGrades: string[]
}

export type PrefillLevel = {
  level: string
  order: number
  /**
   * The level's grades. `subjects` (`grades[].subjects`) are the names pre-selected for that
   * grade (FBISE Grade 11-12: compulsory only); absent when the API does not send them, in which
   * case the grade starts with the level's `subjects`.
   */
  grades: Array<{name: string; order: number; subjects?: string[]}>
  schoolTiming: {startTime: string; endTime: string} | null
  lectureMinutes: number | null
  periodsPerDay: number | null
  breaks: Array<{name: string; startTime: string; endTime: string}>
  /** Default-selected subjects only; can be [] (AS/A Level: the school picks). */
  subjects: PrefillSubject[]
  /**
   * Every subject the level offers (`available_subjects`). Optional: prefills cached before the
   * field existed lack it, and `subjects` stands in then.
   */
  availableSubjects?: PrefillAvailableSubject[]
  subjectsSource: string | null
}

export type CurriculumPrefill = {
  curriculumCode: string | null
  curriculumName: string | null
  /** `curriculum.short_code` ("FBISE"); sent whether or not names are prefixed. */
  curriculumShortCode?: string | null
  country: {code: string; name: string} | null
  /** ISO-8601 weekday numbers, 1 = Monday … 7 = Sunday. */
  workingDays: number[]
  halfDays: number[]
  schoolWeekNote: string | null
  session: {period: string; startDate: string; endDate: string} | null
  levels: PrefillLevel[]
  sources: PrefillSources
  /** ISO timestamp of the fetch — session dates are computed server-side against "today". */
  fetchedAt: string
}

/** The wizard's Institution Setup fields a prefill can seed (Step 2 review page). */
export type InstitutionDefaults = {
  session: {name: string; start: string; end: string} | null
  timings: Array<{day: string; startTime: string; endTime: string; lectureDuration: number}>
  breaks: Array<{day: string; startTime: string; endTime: string}>
  /** The level `timings` and `breaks` were taken from (the first one with a school timing). */
  timingsLevel: string | null
  /** Another level starts/finishes at other times or has other breaks than `timingsLevel`. */
  timingsDiffer: boolean
  /** Wizard weekday names of the half days, for labelling — the prefill has no early end time. */
  halfDays: string[]
  schoolWeekNote: string | null
  sources: PrefillSources
}

/** ISO weekday (1–7) → the wizard's weekday constant. */
const ISO_WEEKDAYS = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const

const str = (value: unknown): string => String(value ?? '').trim()
const num = (value: unknown): number | null => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}
/** Keep `HH:MM` only — the dataset stores minutes, but a `HH:MM:SS` would break the time inputs. */
const hhmm = (value: unknown): string => {
  const raw = str(value)
  const match = raw.match(/^(\d{1,2}):(\d{2})/)
  return match ? `${match[1].padStart(2, '0')}:${match[2]}` : raw
}
const lower = (value: unknown) => str(value).toLowerCase()
/** A subject (or group/grade) entry's name: a plain string or `{name}` / `{subject}`. */
const subjectName = (value: unknown): string =>
  typeof value === 'string' || typeof value === 'number'
    ? str(value)
    : str((value as any)?.name ?? (value as any)?.subject)
/** Names of a list of subject/group/grade entries, blanks and repeats dropped. */
const subjectNames = (value: unknown): string[] =>
  Array.from(new Set((Array.isArray(value) ? value : []).map(subjectName).filter(Boolean)))

/** fetchData hands back the parsed JSON; every endpoint here wraps its payload in `result`. */
const unwrap = (res: unknown): Record<string, any> => {
  const body = (res as any)?.result ?? res
  return body && typeof body === 'object' ? body : {}
}

/**
 * Country name → ISO alpha-2 from the bundled table, or null. Both endpoints accept a name as
 * well, but the code side-steps any spelling drift between the signup list and the dataset.
 */
export const alpha2For = (name: string | null | undefined): string | null => {
  const needle = str(name).toLowerCase()
  if (!needle) return null
  if (/^[a-z]{2}$/.test(needle) && COUNTRY_CODES[needle.toUpperCase()]) return needle.toUpperCase()
  const hit = Object.entries(COUNTRY_CODES).find(([, n]) => n.toLowerCase() === needle)
  return hit ? hit[0] : null
}

export const isoDaysToWeekdays = (days: unknown): string[] =>
  (Array.isArray(days) ? days : [])
    .map((d) => num(d))
    .filter((d): d is number => d !== null && d >= 1 && d <= 7)
    .map((d) => ISO_WEEKDAYS[d - 1])

// ---------------------------------------------------------------------------------------------
// Normalisers
// ---------------------------------------------------------------------------------------------

export const normalizeCurriculaResponse = (res: unknown): CurriculaCatalog => {
  const body = unwrap(res)
  const country =
    body.country && str(body.country.code)
      ? {code: str(body.country.code), name: str(body.country.name)}
      : null
  const toOption = (row: any): CurriculumOption | null => {
    const code = str(row?.code)
    const name = str(row?.name)
    if (!code || !name) return null
    return {
      code,
      name,
      scope: lower(row?.scope) === 'international' ? 'international' : 'national',
      awardingBody: str(row?.awarding_body) || null,
      country: str(row?.country) || null,
      shortCode: str(row?.short_code) || null,
    }
  }
  const notNull = (row: CurriculumOption | null): row is CurriculumOption => row !== null
  const curricula: CurriculumOption[] = (Array.isArray(body.curricula) ? body.curricula : [])
    .map(toOption)
    .filter(notNull)
  // Resolved through `curricula` by code first: selections are keyed by name, so a chip must
  // carry the exact name the full list uses. The API says every common code is also in
  // `curricula`, but a common entry the list does not know (e.g. another country's board
  // listed as common) is still shown — it is added to `curricula` too, so "View all", the
  // name → code lookup and the prefill all work for it. Repeats are collapsed.
  const byCode = new Map(curricula.map((c) => [c.code, c]))
  const seenCommon = new Set<string>()
  // Ordered by `rank` (1 = most common); rows without one keep their API order, after the ranked.
  const rankOf = (row: any) => num(row?.rank) ?? Number.POSITIVE_INFINITY
  const commonCurricula: CurriculumOption[] = (
    Array.isArray(body.common_curricula) ? body.common_curricula : []
  )
    .map((row: any, index: number) => ({row, index}))
    .sort(
      (a: {row: any; index: number}, b: {row: any; index: number}) =>
        rankOf(a.row) - rankOf(b.row) || a.index - b.index
    )
    .map(({row}: {row: any}): CurriculumOption | null => {
      const known = byCode.get(str(row?.code))
      if (known) return known
      const option = toOption(row)
      if (option && !curricula.some((c) => c.name === option.name)) {
        curricula.push(option)
        byCode.set(option.code, option)
      }
      return option ? (byCode.get(option.code) ?? null) : null
    })
    .filter(notNull)
    .filter((c: CurriculumOption) => (seenCommon.has(c.code) ? false : !!seenCommon.add(c.code)))
  const source = lower(body.common_curricula_source)
  const nationalCount = num(body.national_count)
  return {
    commonCurricula,
    commonCurriculaSource: source === 'curated' || source === 'default' ? source : null,
    country,
    nationalCount:
      nationalCount !== null
        ? nationalCount
        : curricula.filter((c) => c.scope === 'national').length,
    curricula,
  }
}

export const normalizePrefillResponse = (res: unknown): CurriculumPrefill => {
  const body = unwrap(res)
  const levels: PrefillLevel[] = (Array.isArray(body.levels) ? body.levels : [])
    .map((lvl: any, index: number): PrefillLevel | null => {
      const level = str(lvl?.level ?? lvl?.name)
      if (!level) return null
      const timing = lvl?.school_timing
      const start = hhmm(timing?.start_time)
      const end = hhmm(timing?.end_time)
      return {
        level,
        order: num(lvl?.order) ?? index + 1,
        grades: (Array.isArray(lvl?.grades) ? lvl.grades : [])
          .map((g: any, gi: number) => ({
            name: str(g?.name ?? g),
            order: num(g?.order) ?? gi + 1,
            ...(Array.isArray(g?.subjects) ? {subjects: subjectNames(g.subjects)} : {}),
          }))
          .filter((g: {name: string}) => g.name.length > 0)
          .sort((a: {order: number}, b: {order: number}) => a.order - b.order),
        schoolTiming: start && end ? {startTime: start, endTime: end} : null,
        lectureMinutes: num(lvl?.lecture_minutes),
        periodsPerDay: num(lvl?.periods_per_day),
        breaks: (Array.isArray(lvl?.breaks) ? lvl.breaks : [])
          .map((b: any) => ({
            name: str(b?.name),
            startTime: hhmm(b?.start_time),
            endTime: hhmm(b?.end_time),
          }))
          .filter((b: {startTime: string; endTime: string}) => b.startTime && b.endTime),
        subjects: (Array.isArray(lvl?.subjects) ? lvl.subjects : [])
          .map(
            (s: any): PrefillSubject => ({
              name: subjectName(s),
              frequency: str(s?.frequency),
              periodsPerWeek: num(s?.periods_per_week),
              ...(typeof s?.default_selected === 'boolean'
                ? {defaultSelected: s.default_selected}
                : {}),
              ...(str(s?.basis) ? {basis: str(s.basis)} : {}),
              ...(str(s?.frequency_source) ? {frequencySource: str(s.frequency_source)} : {}),
            })
          )
          .filter((s: PrefillSubject) => s.name.length > 0),
        availableSubjects: (Array.isArray(lvl?.available_subjects) ? lvl.available_subjects : [])
          .map(
            (s: any): PrefillAvailableSubject => ({
              name: subjectName(s),
              frequency: str(s?.frequency),
              periodsPerWeek: num(s?.periods_per_week),
              groups: subjectNames(s?.groups),
              grades: subjectNames(s?.grades),
              defaultGrades: subjectNames(s?.default_grades),
            })
          )
          .filter((s: PrefillAvailableSubject) => s.name.length > 0),
        subjectsSource: str(lvl?.subjects_source) || null,
      }
    })
    .filter((lvl: PrefillLevel | null): lvl is PrefillLevel => lvl !== null)
    .sort((a: PrefillLevel, b: PrefillLevel) => a.order - b.order)

  const session = body.session
  const sources: PrefillSources = {}
  Object.entries(body.sources ?? {}).forEach(([key, value]) => {
    const v = str(value)
    if (v) sources[key] = v
  })

  return {
    curriculumCode: str(body.curriculum?.code) || null,
    curriculumName: str(body.curriculum?.name) || null,
    curriculumShortCode: str(body.curriculum?.short_code) || null,
    country:
      body.country && str(body.country.code)
        ? {code: str(body.country.code), name: str(body.country.name)}
        : null,
    workingDays: (Array.isArray(body.working_days) ? body.working_days : [])
      .map((d: unknown) => num(d))
      .filter((d: number | null): d is number => d !== null),
    halfDays: (Array.isArray(body.half_days) ? body.half_days : [])
      .map((d: unknown) => num(d))
      .filter((d: number | null): d is number => d !== null),
    schoolWeekNote: str(body.school_week_note) || null,
    session:
      session && str(session.start_date) && str(session.end_date)
        ? {
            period: str(session.period),
            startDate: str(session.start_date),
            endDate: str(session.end_date),
          }
        : null,
    levels,
    sources,
    fetchedAt: new Date().toISOString(),
  }
}

// ---------------------------------------------------------------------------------------------
// Projections onto the wizard's shapes
// ---------------------------------------------------------------------------------------------

/**
 * Levels → classes → subjects, the shape `buildAcademicStructure` / Assign Subjects read. Each
 * grade becomes a class. A class's subjects are the PRE-SELECTED ones only: the grade's own
 * `subjects` when the API sends them (FBISE Grade 11-12: compulsory only), else the level's
 * default-selected `subjects` (which can be empty: AS/A Level). The level's full offer
 * (`available_subjects`, plus anything pre-selected) is kept apart in `availableSubjects` for
 * the level drawer's chip list, and is never selected by itself.
 */
export const prefillToLevelDetails = (
  prefill: CurriculumPrefill
): Record<string, StoredLevelDetail> => {
  const output: Record<string, StoredLevelDetail> = {}
  prefill.levels.forEach((lvl) => {
    const subjects = Array.from(new Set(lvl.subjects.map((s) => s.name)))
    const classes = lvl.grades.map((g) => ({
      name: g.name,
      subjects: [...(Array.isArray(g.subjects) ? g.subjects : subjects)],
    }))
    output[lvl.level] = {
      classes,
      availableSubjects: Array.from(
        new Set([
          ...(lvl.availableSubjects ?? []).map((s) => s.name),
          ...subjects,
          ...classes.flatMap((cls) => cls.subjects),
        ])
      ),
    }
  })
  return output
}

/**
 * `level (lower) → subject (lower) → "5/week"`, for Lesson Planning's per-cell default. Covers
 * every subject the level offers; a default-selected subject's own frequency wins.
 */
export const prefillFrequencyMap = (
  prefill: CurriculumPrefill
): Record<string, Record<string, string>> => {
  const output: Record<string, Record<string, string>> = {}
  prefill.levels.forEach((lvl) => {
    const bySubject: Record<string, string> = {}
    ;[...(lvl.availableSubjects ?? []), ...lvl.subjects].forEach((s) => {
      if (s.frequency) bySubject[lower(s.name)] = s.frequency
    })
    output[lower(lvl.level)] = bySubject
  })
  return output
}

/** "10:30-10:50,12:00-12:15": a level's breaks as one comparable key. */
const breaksKey = (breaks: ReadonlyArray<{startTime: string; endTime: string}>) =>
  breaks
    .map((b) => `${b.startTime}-${b.endTime}`)
    .sort()
    .join(',')

/**
 * Institution Setup defaults from a prefill. The wizard keeps ONE set of school timings for the
 * whole institution (per weekday), while the dataset is per level — the first level that carries
 * a school timing stands in for the school (`timingsLevel`), and `timingsDiffer` says whether any
 * other level starts/finishes at other times or has other breaks, so the review page can say
 * where the hours came from (P7-3). Timings and breaks are emitted for each working day; half
 * days get the same hours (the dataset has no early end time) and are returned separately for
 * labelling.
 */
export const prefillInstitutionDefaults = (prefill: CurriculumPrefill): InstitutionDefaults => {
  const days = isoDaysToWeekdays(prefill.workingDays)
  const lead = prefill.levels.find((lvl) => lvl.schoolTiming !== null) ?? null
  const timingsDiffer =
    lead !== null &&
    prefill.levels.some(
      (lvl) =>
        lvl !== lead &&
        lvl.schoolTiming !== null &&
        (lvl.schoolTiming.startTime !== lead.schoolTiming!.startTime ||
          lvl.schoolTiming.endTime !== lead.schoolTiming!.endTime ||
          breaksKey(lvl.breaks) !== breaksKey(lead.breaks))
    )
  const timings =
    lead && lead.schoolTiming
      ? days.map((day) => ({
          day,
          startTime: lead.schoolTiming!.startTime,
          endTime: lead.schoolTiming!.endTime,
          lectureDuration: lead.lectureMinutes ?? 0,
        }))
      : []
  const breaks = lead
    ? days.flatMap((day) =>
        lead.breaks.map((b) => ({day, startTime: b.startTime, endTime: b.endTime}))
      )
    : []
  return {
    session: prefill.session
      ? {
          name: prefill.session.period,
          start: prefill.session.startDate,
          end: prefill.session.endDate,
        }
      : null,
    timings,
    breaks,
    timingsLevel: lead?.level ?? null,
    timingsDiffer,
    halfDays: isoDaysToWeekdays(prefill.halfDays),
    schoolWeekNote: prefill.schoolWeekNote,
    sources: prefill.sources,
  }
}

/**
 * The level whose hours the school timings on screen were taken from, when other levels keep
 * different hours (P7-3) — or null. Null as soon as the timings no longer match that level's
 * (the user changed them), since the note would no longer be true.
 */
export const timingsFromLevel = (
  defaults: InstitutionDefaults | null | undefined,
  timings: ReadonlyArray<{startTime?: unknown; endTime?: unknown}>
): string | null => {
  const lead = defaults?.timings[0]
  if (!defaults?.timingsDiffer || !defaults.timingsLevel || !lead || timings.length === 0)
    return null
  const matches = timings.every(
    (t) => hhmm(t?.startTime) === lead.startTime && hhmm(t?.endTime) === lead.endTime
  )
  return matches ? defaults.timingsLevel : null
}

/** Break rows grouped for the review summary (P7-4). */
export type BreakTimingsSummary = {
  /** Distinct break slots a day. */
  count: number
  /** "10:30-10:50" per slot, earliest first. */
  times: string[]
  /** Wizard weekday constants ('MONDAY' …) with a break, in week order. */
  days: string[]
  /** `days` is one unbroken run of two or more days (shown as "Mon-Fri"). */
  contiguous: boolean
}

/**
 * Groups break rows (one per day × break) by their `startTime-endTime`: a 5-day week with one
 * break is ONE break a day on Monday to Friday, not "5 breaks".
 */
export const summarizeBreakTimings = (
  breaks: ReadonlyArray<{day?: unknown; startTime?: unknown; endTime?: unknown}>
): BreakTimingsSummary => {
  const times = new Set<string>()
  const dayIndexes = new Set<number>()
  breaks.forEach((b) => {
    const start = hhmm(b?.startTime)
    const end = hhmm(b?.endTime)
    if (!start || !end) return
    times.add(`${start}-${end}`)
    const dayIndex = ISO_WEEKDAYS.indexOf(str(b?.day).toUpperCase() as any)
    if (dayIndex >= 0) dayIndexes.add(dayIndex)
  })
  const sortedDays = Array.from(dayIndexes).sort((a, b) => a - b)
  return {
    count: times.size,
    times: Array.from(times).sort(),
    days: sortedDays.map((index) => ISO_WEEKDAYS[index]),
    contiguous:
      sortedDays.length > 1 && sortedDays.every((d, i) => i === 0 || d === sortedDays[i - 1] + 1),
  }
}

// ---------------------------------------------------------------------------------------------
// Cache (per curriculum display name, next to the legacy school-system details)
// ---------------------------------------------------------------------------------------------

export type StoredCurriculumPrefills = Record<string, CurriculumPrefill>

export const readStoredCurriculumPrefills = (): StoredCurriculumPrefills => {
  try {
    const raw = localStorage.getItem(ONBOARDING_CURRICULUM_PREFILL_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

/**
 * Short codes ("Cambridge", "FBISE") for the selected curricula `names` that `current`
 * (`manualData.curriculumShortCodes`) has none for: from the `timetable/curricula` catalogue
 * (`short_code`, matched by catalogue code, then by name), else from a cached
 * `timetable/prefill` (`curriculum.short_code`). Only the missing ones come back; a custom
 * curriculum has none. Without them a resumed wizard shows "Cambridge Grade 1" where the naming
 * rule says "Grade 1" (bugs 375/377).
 */
export const missingCurriculumShortCodes = (
  names: readonly string[],
  current: Record<string, string> | null | undefined,
  sources: {
    catalog?: CurriculaCatalog | null
    codes?: Record<string, string> | null
    prefills?: StoredCurriculumPrefills | null
  }
): Record<string, string> => {
  const found: Record<string, string> = {}
  const options = sources.catalog?.curricula ?? []
  names.forEach((rawName) => {
    const name = str(rawName)
    if (!name || str(current?.[name])) return
    const code = str(sources.codes?.[name])
    const option =
      (code ? options.find((o) => o.code === code) : undefined) ??
      options.find((o) => o.name === name) ??
      options.find((o) => lower(o.name) === lower(name))
    const shortCode =
      str(option?.shortCode) || str(sources.prefills?.[name]?.curriculumShortCode)
    if (shortCode) found[name] = shortCode
  })
  return found
}

export const writeStoredCurriculumPrefills = (prefills: StoredCurriculumPrefills) => {
  localStorage.setItem(ONBOARDING_CURRICULUM_PREFILL_KEY, JSON.stringify(prefills))
}

export const clearStoredCurriculumPrefills = () => {
  localStorage.removeItem(ONBOARDING_CURRICULUM_PREFILL_KEY)
}

/**
 * Prefill frequency for a (curriculum, level, subject) triple from the cache, or null. Lesson
 * Planning uses it as the cell default before falling back to the Step 2 frequency.
 */
export const cachedPrefillFrequency = (
  prefills: StoredCurriculumPrefills,
  curriculum: string,
  level: string,
  subject: string
): string | null => {
  const prefill = prefills[str(curriculum)]
  if (!prefill) return null
  const hit = prefillFrequencyMap(prefill)[lower(level)]?.[lower(subject)]
  return hit || null
}

// ---------------------------------------------------------------------------------------------
// Level naming (FE-3: plain names)
// ---------------------------------------------------------------------------------------------

/** Which names the two caches hold: 'plain' ("Primary") or 'prefixed' ("FBISE Primary"). */
const ONBOARDING_CURRICULUM_NAMING_KEY = 'onboarding_curriculum_naming'

let requestPlainNames = false

/**
 * True when level/grade names are plain ("Primary"): the curriculum lives on the level, so the
 * wizard must not add a curriculum prefix to any name itself either.
 */
export const usesPlainCurriculumNames = () => requestPlainNames

/**
 * Sets whether the prefill calls ask for plain level/grade names (`plain_names=true`). Called
 * once the wizard knows the school's backend support (see levelCurriculum.ts, FE-1). Both
 * caches hold names in one form only, so switching form drops them and the next read refetches.
 */
export const setPlainCurriculumNames = (enabled: boolean) => {
  requestPlainNames = enabled
  const mode = enabled ? 'plain' : 'prefixed'
  try {
    const stored = localStorage.getItem(ONBOARDING_CURRICULUM_NAMING_KEY)
    if (stored === mode) return
    // No marker yet: everything cached so far was fetched without the flag (prefixed).
    if (stored !== null || enabled) {
      clearStoredCurriculumPrefills()
      writeStoredSchoolSystemDetails({})
    }
    localStorage.setItem(ONBOARDING_CURRICULUM_NAMING_KEY, mode)
  } catch {
    // Storage unavailable: the caches are unreadable too, nothing to invalidate.
  }
}

// ---------------------------------------------------------------------------------------------
// Loader
// ---------------------------------------------------------------------------------------------

type DispatchLike = (action: any) => unknown

export type LoadCurriculumDefaultsParams = {
  /** Country name or alpha-2 as the wizard holds it; null lets the API use the tenant's country. */
  country: string | null | undefined
  /** Curriculum display names to resolve. */
  curriculums: string[]
  /** API code by display name; names without one go to the legacy details endpoint. */
  codes: Record<string, string> | undefined
}

/**
 * Resolve curriculum defaults for a set of selected curricula and merge them into both caches.
 * Names with a code hit `timetable/prefill` (one call each, in parallel); names without one fall
 * back to `csv-import/school-systems-details` in a single call. Always completes: a curriculum
 * whose request fails is simply absent from the result, which every caller already treats as
 * "no defaults for this one".
 */
export const loadCurriculumDefaults = (
  dispatch: DispatchLike,
  params: LoadCurriculumDefaultsParams,
  onDone: (details: StoredSchoolSystemDetails, prefills: StoredCurriculumPrefills) => void
) => {
  const names = Array.from(new Set(params.curriculums.map(str).filter(Boolean)))
  const codes = params.codes ?? {}
  const withCode = names.filter((name) => str(codes[name]).length > 0)
  const legacy = names.filter((name) => str(codes[name]).length === 0)

  const details: StoredSchoolSystemDetails = {}
  const prefills: StoredCurriculumPrefills = {}
  let pending = withCode.length + (legacy.length > 0 ? 1 : 0)

  const settle = () => {
    pending -= 1
    if (pending > 0) return
    const nextDetails = {...readStoredSchoolSystemDetails(), ...details}
    writeStoredSchoolSystemDetails(nextDetails)
    if (Object.keys(prefills).length > 0) {
      writeStoredCurriculumPrefills({...readStoredCurriculumPrefills(), ...prefills})
    }
    onDone(details, prefills)
  }

  if (pending === 0) {
    onDone({}, {})
    return
  }

  withCode.forEach((name) => {
    dispatch(
      getTimetablePrefill(
        {
          countryCode: alpha2For(params.country) ?? params.country ?? null,
          curriculum: codes[name],
          plainNames: requestPlainNames,
        },
        (res: unknown) => {
          const prefill = normalizePrefillResponse(res)
          if (prefill.levels.length > 0) {
            prefills[name] = prefill
            details[name] = prefillToLevelDetails(prefill)
          }
          settle()
        },
        settle
      )
    )
  })

  if (legacy.length > 0) {
    dispatch(
      getSchoolSystemsDetails(
        legacy,
        (res: any) => {
          const normalized = normalizeSchoolSystemDetailsResponse(res?.result ?? [])
          legacy.forEach((name) => {
            if (normalized[name]) details[name] = normalized[name]
          })
          settle()
        },
        settle,
        {plainNames: requestPlainNames}
      )
    )
  }
}
